import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ApiService } from '../services/apiService';
import { CardState } from '../types/cardTypes';
import { getQuote, clearQuote } from '../utils/quoteStorage';
import { StatusPageLayout } from '../components/layout/StatusPageLayout';
// @mcc-dev-preview:import
import { parseDevState, DEV_CARD_ID } from '../dev/devPreview';

// @mcc-dev-preview:start redirect-to-banxa-dev-state
const REDIRECT_TO_BANXA_DEV_STATES = ['verifying', 'creating-order', 'error'] as const;
type RedirectToBanxaDevState = (typeof REDIRECT_TO_BANXA_DEV_STATES)[number];
// @mcc-dev-preview:end redirect-to-banxa-dev-state

const RedirectToBanxa: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cardId = searchParams.get('cardId');
  // @mcc-dev-preview:start redirect-to-banxa-dev-state
  const devState = parseDevState(searchParams, REDIRECT_TO_BANXA_DEV_STATES);
  // @mcc-dev-preview:end redirect-to-banxa-dev-state

  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasAttemptedOrderRef = useRef(false);

  const createOrderFromQuote = React.useCallback(async () => {
    if (!cardId || isCreatingOrder || hasAttemptedOrderRef.current) {
      return;
    }

    const quote = getQuote(cardId);
    if (!quote) {
      setIsLoading(false);
      navigate(`/welcome?cardId=${cardId}`);
      return;
    }

    try {
      setIsCreatingOrder(true);
      hasAttemptedOrderRef.current = true;
      setIsLoading(false);

      const orderResponse = await ApiService.createQuoteOrder({
        cardId,
        fiatAmount: quote.fiatAmount,
        cryptoAmount: quote.cryptoAmount,
        fiatCode: quote.fiatCode,
        coinCode: quote.coinCode,
        paymentMethodId: quote.paymentMethodId,
        processingFee: quote.processingFee,
        networkFee: quote.networkFee,
        blockchain: quote.blockchain
      });

      if (orderResponse.success && orderResponse.data?.checkoutUrl) {
        clearQuote(cardId);
        window.location.href = orderResponse.data.checkoutUrl;
      } else {
        setError(orderResponse.error || 'Failed to create Banxa order. Please try again.');
        setIsCreatingOrder(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create Banxa order. Please try again.');
      setIsCreatingOrder(false);
    }
  }, [cardId, isCreatingOrder, navigate]);

  // @mcc-dev-preview:start redirect-to-banxa-dev-state
  useEffect(() => {
    if (!devState) return;

    hasAttemptedOrderRef.current = devState !== 'error';

    switch (devState) {
      case 'verifying':
        setIsLoading(true);
        setIsCreatingOrder(false);
        setError(null);
        break;
      case 'creating-order':
        setIsLoading(false);
        setIsCreatingOrder(true);
        setError(null);
        break;
      case 'error':
        setIsLoading(false);
        setIsCreatingOrder(false);
        setError(
          searchParams.get('errorMessage') ??
            'Failed to create Banxa order. Please try again.'
        );
        break;
      default:
        break;
    }
  }, [devState, searchParams]);
  // @mcc-dev-preview:end redirect-to-banxa-dev-state

  useEffect(() => {
    // @mcc-dev-preview:start redirect-to-banxa-dev-state
    if (devState) return;
    // @mcc-dev-preview:end redirect-to-banxa-dev-state

    const fetchCardData = async () => {
      if (!cardId) {
        setError('Card ID is required');
        setIsLoading(false);
        return;
      }

      if (hasAttemptedOrderRef.current) {
        return;
      }

      try {
        const cardData = await ApiService.getCardByCardId(cardId);
        if (cardData?.state === CardState.READY_FOR_PURCHASE) {
          createOrderFromQuote();
        } else {
          setError(`Card is not ready for purchase. Current state: ${cardData?.state || 'unknown'}`);
          setIsLoading(false);
        }
      } catch {
        setError('Failed to load card details');
        setIsLoading(false);
      }
    };

    fetchCardData();
  }, [cardId, createOrderFromQuote, devState]);

  const previewCardId = cardId ?? (devState ? DEV_CARD_ID : null);

  if (isLoading || isCreatingOrder) {
    return (
      <StatusPageLayout
        variant="loading"
        title={isLoading ? 'Verifying your card' : 'Creating your order'}
        body={
          isLoading
            ? 'Please wait while we verify your card.'
            : 'Please wait while we prepare your Banxa checkout. You will be redirected shortly.'
        }
      />
    );
  }

  if (error) {
    return (
      <StatusPageLayout
        variant="error"
        title="Something went wrong"
        body={error}
        actions={
          previewCardId ? (
            <button
              type="button"
              className="ev-btn-primary"
              onClick={() => {
                // @mcc-dev-preview:start redirect-to-banxa-dev-state
                if (devState) return;
                // @mcc-dev-preview:end redirect-to-banxa-dev-state
                setError(null);
                hasAttemptedOrderRef.current = false;
                setIsLoading(true);
                const fetchAndRetry = async () => {
                  try {
                    const cardData = await ApiService.getCardByCardId(previewCardId);
                    if (cardData?.state === CardState.READY_FOR_PURCHASE) {
                      setIsLoading(false);
                      createOrderFromQuote();
                    } else {
                      setError(`Card is not ready for purchase. Current state: ${cardData?.state || 'unknown'}`);
                      setIsLoading(false);
                    }
                  } catch {
                    setError('Failed to load card details');
                    setIsLoading(false);
                  }
                };
                fetchAndRetry();
              }}
            >
              Retry
            </button>
          ) : undefined
        }
      />
    );
  }

  return null;
};

export default RedirectToBanxa;
